// The Fragen-Check's view logic as pure functions over the API shapes: the
// words for the keys the backend sends, the order of the groups, the «nach
// Frageart» grouping, the tri-state selection and the result summaries.

import {
  type CheckResult,
  GROUP_KINDS,
  type GroupKind,
  type QuestionCheck,
  type QuestionCheckEstimate,
  type QuestionGroup,
  type QuestionRow,
  type QuestionType,
  type TopicKey,
  type Verdict,
  VERDICTS,
} from "@/api/types";
import { t, tOr, type TranslationKey } from "@/i18n";
import { formatDecimal, formatDurationSeconds, formatInt } from "@/lib/format";

/** Questions per check (QuestionCheckCreate.rows); the estimate reports the same bound. */
export const MAX_QUESTIONS = 100;

/** Questions one `/next` step runs together (question_check.STEP_SIZE). */
export const QUESTIONS_PER_STEP = 3;

/** What `n` questions cost and take, by the backend's rule on the per-question
 * basis of its estimate: every question at `per_question_usd` (the search
 * included), and a step's time per three questions. The basis is the same for
 * every count, so it is asked for once and multiplied here. */
export function estimateTotals(
  basis: Pick<QuestionCheckEstimate, "per_question_usd" | "seconds_per_question">,
  n: number,
): { usd: number; seconds: number } {
  return {
    usd: n * basis.per_question_usd,
    seconds: basis.seconds_per_question * Math.ceil(n / QUESTIONS_PER_STEP),
  };
}

export const KIND_LABEL: Record<GroupKind, TranslationKey> = {
  beruf: "qc.kind.beruf",
  begriff: "qc.kind.begriff",
  thema: "qc.kind.thema",
  kein_bildungsthema: "qc.kind.kein_bildungsthema",
  unvollstaendig: "qc.kind.unvollstaendig",
  nicht_zugeordnet: "qc.kind.nicht_zugeordnet",
};

export const TOPIC_LABEL: Record<TopicKey, TranslationKey> = {
  berufswahl: "qc.topic.berufswahl",
  bewerbung: "qc.topic.bewerbung",
  lehrstellensuche: "qc.topic.lehrstellensuche",
  ueberblick: "qc.topic.ueberblick",
  zukunft: "qc.topic.zukunft",
  lohn: "qc.topic.lohn",
  dauer: "qc.topic.dauer",
  nach_der_lehre: "qc.topic.nach_der_lehre",
};

export const QTYPE_LABEL: Record<QuestionType, TranslationKey> = {
  was_ist: "qc.qtype.was_ist",
  berufsbild: "qc.qtype.berufsbild",
  alltag: "qc.qtype.alltag",
  lohn: "qc.qtype.lohn",
  dauer: "qc.qtype.dauer",
  voraussetzungen: "qc.qtype.voraussetzungen",
  berufswahl: "qc.qtype.berufswahl",
  weiterbildung: "qc.qtype.weiterbildung",
  bewerbung: "qc.qtype.bewerbung",
  lehrstelle: "qc.qtype.lehrstelle",
  ort: "qc.qtype.ort",
  vergleich: "qc.qtype.vergleich",
  anmeldung: "qc.qtype.anmeldung",
  stichwort: "qc.qtype.stichwort",
  sonstige: "qc.qtype.sonstige",
};

export const VERDICT_LABEL: Record<Verdict, TranslationKey> = {
  error: "qc.verdict.error",
  no_page: "qc.verdict.no_page",
  support: "qc.verdict.support",
  weak: "qc.verdict.weak",
  uncited: "qc.verdict.uncited",
  answered: "qc.verdict.answered",
};

// A key a newer backend adds reads as itself, never as an empty cell.
function lookup(labels: Readonly<Record<string, TranslationKey>>, key: string): string {
  return Object.hasOwn(labels, key) ? t(labels[key]) : key;
}

export const kindLabel = (kind: string): string => lookup(KIND_LABEL, kind);
export const topicLabel = (topic: string): string => lookup(TOPIC_LABEL, topic);
export const qtypeLabel = (qtype: string): string => lookup(QTYPE_LABEL, qtype);
export const verdictLabel = (verdict: string): string => lookup(VERDICT_LABEL, verdict);

const HTTP_ERROR = /^http_(\d{3})$/;

/** A result's error in words (api/types RESULT_ERRORS): `http_<status>` with
 * its status, a known code by name, any other code as the backend wrote it. */
export function resultErrorLabel(code: string): string {
  const http = HTTP_ERROR.exec(code);
  if (http) return t("qc.resultError.http", { status: http[1] });
  return tOr(`qc.resultError.${code}`, code);
}

/** A group's name: its data label as it is, else its translated topic or kind. */
export function groupLabel(group: Pick<QuestionGroup, "kind" | "label" | "topic">): string {
  if (group.label) return group.label;
  return group.topic ? topicLabel(group.topic) : kindLabel(group.kind);
}

/** The kind a group key («<kind>:<value>») names, if it names one. */
function kindOfKey(key: string): GroupKind | null {
  const prefix = key.split(":", 1)[0];
  return GROUP_KINDS.find((kind) => kind === prefix) ?? null;
}

export interface GroupNames {
  label: string;
  kind: string;
}

/** A group's name and kind in words, from the list's groups; a group the list
 * no longer carries reads as its key's value, else as its key's kind. */
export function groupNamesOf(
  key: string,
  stored: ReadonlyMap<string, QuestionGroup>,
): GroupNames {
  const group = stored.get(key);
  if (group) return { label: groupLabel(group), kind: kindLabel(group.kind) };
  const cut = key.indexOf(":");
  const kind = kindLabel(cut < 0 ? key : key.slice(0, cut));
  return { label: (cut < 0 ? "" : key.slice(cut + 1)) || kind, kind };
}

// These sort last, in this order.
const TAIL_KINDS: readonly GroupKind[] = ["kein_bildungsthema", "unvollstaendig"];
const tailRank = (kind: GroupKind | null): number => (kind ? TAIL_KINDS.indexOf(kind) + 1 : 0);

/** Most impressions first, the two tail kinds last; ties keep their order. */
function byImpressions<T extends { kind: GroupKind | null; impressions: number }>(
  items: readonly T[],
): T[] {
  return [...items].sort(
    (a, b) => tailRank(a.kind) - tailRank(b.kind) || b.impressions - a.impressions,
  );
}

export type GroupingMode = "keyword" | "type";

/** One line of the groups table: a stored group, or one question type. */
export interface GroupView {
  key: string;
  label: string;
  /** The stored group's kind; null for a question type. */
  kind: GroupKind | null;
  rows: QuestionRow[];
  clicks: number;
  impressions: number;
  ctr: number | null;
  position: number | null;
}

/** The stored groups with their questions, in the order they show. */
export function keywordGroups(
  groups: readonly QuestionGroup[],
  rows: readonly QuestionRow[],
): GroupView[] {
  const byN = new Map(rows.map((row) => [row.n, row]));
  return byImpressions(
    groups.map((group) => ({
      key: group.key,
      label: groupLabel(group),
      kind: group.kind,
      rows: group.rows.flatMap((n) => byN.get(n) ?? []),
      clicks: group.clicks,
      impressions: group.impressions,
      ctr: group.ctr,
      position: group.position,
    })),
  );
}

// The type of the rows no rule recognised; it sorts last.
const CATCH_ALL_KEY = "qtype:sonstige";

/** The same rows by question type: most impressions first, «sonstige» last. */
export function typeGroups(rows: readonly QuestionRow[]): GroupView[] {
  const byType = new Map<QuestionType, QuestionRow[]>();
  for (const row of rows) {
    const members = byType.get(row.qtype);
    if (members) members.push(row);
    else byType.set(row.qtype, [row]);
  }
  const views = [...byType].map(
    ([qtype, members]): GroupView => ({
      key: `qtype:${qtype}`,
      label: qtypeLabel(qtype),
      kind: null,
      rows: [...members].sort((a, b) => b.impressions - a.impressions),
      ...rowTotals(members),
    }),
  );
  const last = (view: GroupView) => (view.key === CATCH_ALL_KEY ? 1 : 0);
  return views.sort((a, b) => last(a) - last(b) || b.impressions - a.impressions);
}

/** The mean Google position of the rows that carry one, weighted by impressions. */
function meanPosition(rows: readonly QuestionRow[]): number | null {
  const placed = rows.flatMap((row) =>
    row.position === null ? [] : [{ position: row.position, weight: row.impressions }],
  );
  if (placed.length === 0) return null;
  const weight = placed.reduce((sum, p) => sum + p.weight, 0);
  if (weight === 0) return placed.reduce((sum, p) => sum + p.position, 0) / placed.length;
  return placed.reduce((sum, p) => sum + p.position * p.weight, 0) / weight;
}

/** Clicks and impressions summed, the CTR over them, the weighted position. */
function rowTotals(
  rows: readonly QuestionRow[],
): Pick<GroupView, "clicks" | "impressions" | "ctr" | "position"> {
  const clicks = rows.reduce((sum, row) => sum + row.clicks, 0);
  const impressions = rows.reduce((sum, row) => sum + row.impressions, 0);
  return {
    clicks,
    impressions,
    ctr: impressions > 0 ? clicks / impressions : null,
    position: meanPosition(rows),
  };
}

export type Ticked = "none" | "some" | "all";

/** How much of `rows` is ticked. */
export function tickedState(rows: readonly QuestionRow[], selected: ReadonlySet<number>): Ticked {
  const ticked = rows.filter((row) => selected.has(row.n)).length;
  if (ticked === 0) return "none";
  return ticked === rows.length ? "all" : "some";
}

/** `selected` with the rows `ns` ticked (on) or unticked. */
export function withRows(
  selected: ReadonlySet<number>,
  ns: readonly number[],
  on: boolean,
): Set<number> {
  const next = new Set(selected);
  for (const n of ns) {
    if (on) next.add(n);
    else next.delete(n);
  }
  return next;
}

const verdictRank = (verdict: Verdict): number => {
  const rank = VERDICTS.indexOf(verdict);
  return rank < 0 ? VERDICTS.length : rank;
};

/** Problems first, in the verdicts' order of precedence, then the most impressions. */
export function orderResults(results: readonly CheckResult[]): CheckResult[] {
  return [...results].sort(
    (a, b) =>
      verdictRank(a.verdict) - verdictRank(b.verdict) ||
      b.impressions - a.impressions ||
      a.n - b.n,
  );
}

export interface GroupSummary {
  key: string;
  label: string;
  questions: number;
  verdicts: Record<Verdict, number>;
  /** How many different best pages the group's questions got. */
  pages: number;
}

const noVerdicts = (): Record<Verdict, number> => ({
  error: 0,
  no_page: 0,
  support: 0,
  weak: 0,
  uncited: 0,
  answered: 0,
});

/** Per group: its questions per verdict and their distinct best pages, in the order of the groups. */
export function groupSummaries(
  results: readonly CheckResult[],
  groups: readonly QuestionGroup[],
): GroupSummary[] {
  const stored = new Map(groups.map((group) => [group.key, group]));
  const byGroup = new Map<string, CheckResult[]>();
  for (const result of results) {
    const members = byGroup.get(result.group);
    if (members) members.push(result);
    else byGroup.set(result.group, [result]);
  }
  const lines = [...byGroup].map(([key, members]) => {
    const group = stored.get(key);
    const verdicts = noVerdicts();
    for (const member of members) {
      if (Object.hasOwn(verdicts, member.verdict)) verdicts[member.verdict] += 1;
    }
    const summary: GroupSummary = {
      key,
      label: groupNamesOf(key, stored).label,
      questions: members.length,
      verdicts,
      pages: new Set(members.map((member) => member.best_url).filter(Boolean)).size,
    };
    return {
      summary,
      kind: group?.kind ?? kindOfKey(key),
      impressions: group?.impressions ?? members.reduce((sum, m) => sum + m.impressions, 0),
    };
  });
  return byImpressions(lines).map((line) => line.summary);
}

/** «12 von 50 geprüft, 1 fehlgeschlagen»: done and failed are apart. */
export const progressText = (check: Pick<QuestionCheck, "done" | "total" | "failed">): string =>
  t("qc.run.progress", {
    done: formatInt(check.done),
    total: formatInt(check.total),
    failed: formatInt(check.failed),
  });

/** An estimated duration in whole minutes, rounded up: «1 Min», «1 Std 5 Min». */
export const approxDuration = (seconds: number): string =>
  formatDurationSeconds(Math.max(1, Math.ceil(seconds / 60)) * 60);

/** A CTR (0 to 1) as a Swiss percentage, «6,4 %»; «—» where the list carried none. */
export const formatCtr = (ctr: number | null): string =>
  ctr === null ? "—" : `${formatDecimal(ctr * 100)} %`;

/** An average Google position with one decimal; «—» where the list carried none. */
export const formatPosition = (position: number | null): string =>
  position === null ? "—" : formatDecimal(position);
